import { z } from 'zod';

export const CAMPAIGN_CONFIG_TEXT_MAX_LENGTH = 200;

const campaignNameSchema = z
  .preprocess(
    (value): unknown => (value === null ? '' : value),
    z
      .string()
      .trim()
      .max(
        CAMPAIGN_CONFIG_TEXT_MAX_LENGTH,
        `Campaign name must be ${CAMPAIGN_CONFIG_TEXT_MAX_LENGTH} characters or fewer.`,
      ),
  )
  .transform((value): string | null => (value.length > 0 ? value : null));

const productLineSchema = z
  .preprocess(
    (value): unknown => (value === null ? '' : value),
    z
      .string()
      .trim()
      .max(
        CAMPAIGN_CONFIG_TEXT_MAX_LENGTH,
        `Product line must be ${CAMPAIGN_CONFIG_TEXT_MAX_LENGTH} characters or fewer.`,
      ),
  )
  .transform((value): string | null => (value.length > 0 ? value : null));

/** The only campaign configuration fields this identity slice may read or write. */
export const campaignConfigSchema = z.object({
  campaign_name: campaignNameSchema,
  product_line: productLineSchema,
});

export type CampaignConfigFormValues = z.infer<typeof campaignConfigSchema>;

/** Raw identity projection returned by the os_campaign_config read hook. */
export interface CampaignConfigIdentityRow {
  campaign_name: string | null;
  product_line: string | null;
}

export type CampaignConfigDirtyFields = Readonly<
  Partial<Record<keyof CampaignConfigFormValues, boolean>>
>;

export interface CampaignConfigUpsertContext {
  organizationId: string;
  campaignId: string;
}

/**
 * Deliberately excludes archive, mapping, AI-behaviour, and database-managed columns.
 * Optional identity properties make the write a field-level patch rather than a stale
 * round trip of the shared organization row.
 */
export interface CampaignConfigUpsert {
  organization_id: string;
  heyreach_campaign_id: string;
  campaign_name?: string | null;
  product_line?: string | null;
}

export const toCampaignConfigFormValues = (
  row: CampaignConfigIdentityRow | null,
): CampaignConfigFormValues => ({
  campaign_name: row?.campaign_name ?? '',
  product_line: row?.product_line ?? '',
});

export const toCampaignConfigUpsert = (
  formValues: CampaignConfigFormValues,
  dirtyFields: CampaignConfigDirtyFields,
  { organizationId, campaignId }: CampaignConfigUpsertContext,
): CampaignConfigUpsert | null => {
  if (dirtyFields.campaign_name !== true && dirtyFields.product_line !== true) {
    return null;
  }

  // Keep this boundary fail-closed even when called outside RHF's resolver.
  // Zod also strips unexpected keys before any payload is constructed.
  const parsed = campaignConfigSchema.safeParse(formValues);
  if (!parsed.success) {
    return null;
  }

  const payload: CampaignConfigUpsert = {
    organization_id: organizationId,
    heyreach_campaign_id: campaignId,
  };

  if (dirtyFields.campaign_name === true) {
    payload.campaign_name = parsed.data.campaign_name;
  }

  if (dirtyFields.product_line === true) {
    payload.product_line = parsed.data.product_line;
  }

  return payload;
};
